import React, { useState, useEffect } from 'react';
import {
	Box,
	TextField,
	Button,
	IconButton,
	Typography,
	Card,
	CardContent,
	CardActions,
	Grid,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axiosConfig from '@/data/utils/axiosConfigInstance';
import Toast from '@/utils/toastManager';
import DurationPicker from '@/AdminComponent/AddVideoForm/Steps/DurationPicker';
import { secondsToTime, timeToSeconds, getMaxTime } from '@/utils/timeFormat';

const ChaptersModal = ({ open, onClose, videoId, playbackId, videoDuration }) => {
	const [chapters, setChapters] = useState([]);
	const [thumbnailLoading, setThumbnailLoading] = useState({});
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

	useEffect(() => {
		if (open && videoId) {
			fetchChapters();
		}
	}, [open, videoId]);

	const fetchChapters = async () => {
		setLoading(true);
		try {
			const res = await axiosConfig.get(`${import.meta.env.VITE_APP_API_URL}/chapters/mux-chapters/${videoId}`);
			const fetchedChapters = res?.data?.data || [];
			if (fetchedChapters.length > 0) {
				// Convert timecode to HH:MM:SS format for display
				const formattedChapters = fetchedChapters.map(ch => ({
					...ch,
				timecode: secondsToTime(ch.timecode)
			}));
			setChapters(formattedChapters);
		} else {
			setChapters([{ title: '', timecode: '00:00:00', thumbnail: null }]);
		}
	} catch (err) {
		setChapters([{ title: '', timecode: '00:00:00', thumbnail: null }]);
	} finally {
		setLoading(false);
	}
};

const handleChange = (index, field, value) => {
		if (field === 'timecode') {
			const seconds = timeToSeconds(value);

			if (!Number.isFinite(seconds)) {
				Toast.error('Invalid time. Use HH:MM:SS.');
				return;
			}

			const isDuplicate = chapters.some((ch, i) => i !== index && timeToSeconds(ch.timecode) === seconds);
			if (isDuplicate) {
				Toast.error('Each chapter must have a unique time.');
				return;
			}

			if (videoDuration && seconds > videoDuration) {
				Toast.error('Chapter time cannot exceed video duration.');
				return;
			}
		}

		const updated = [...chapters];
		updated[index][field] = value;
		setChapters(updated);
	};

	const handleAddChapter = () => {
		setChapters([...chapters, { title: '', timecode: '00:00:00', thumbnail: null }]);
	};

	const handleRemoveChapter = (index) => {
		const updated = chapters.filter((_, i) => i !== index);
		setChapters(updated);
	};

	const generateThumbnail = async (index) => {
		const chapter = chapters[index];
		if (!chapter.timecode || !playbackId) {
			Toast.error('Required fields are missing');
			return;
		}

		setThumbnailLoading(prev => ({ ...prev, [index]: true }));

		try {
			const seconds = timeToSeconds(chapter.timecode);
			const res = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chapters/create-thumbnails`, {
				playbackId: playbackId,
				timecode: seconds
			});

			const updated = [...chapters];
			updated[index].thumbnail = res?.data?.data;
			setChapters(updated);
		} catch (err) {
			Toast.error('Failed to generate thumbnail');
		} finally {
			setThumbnailLoading(prev => ({ ...prev, [index]: false }));
		}
	};

	const handleSave = async () => {
		const validChapters = chapters.filter(ch => ch.title?.trim() !== '');
		
		if (validChapters.length === 0) {
			Toast.error('Please add at least one chapter with a title');
			return;
		}

		const chaptersToSave = validChapters.map(ch => ({
			title: ch.title,
			timecode: timeToSeconds(ch.timecode),
			thumbnail: ch.thumbnail || null
		}));

		setSaving(true);
		try {
			await axiosConfig.put(`${import.meta.env.VITE_APP_API_URL}/chapters/update/${videoId}`, {
				chapters: chaptersToSave
			});
			Toast.success('Chapters saved successfully');
			onClose();
		} catch (err) {
			Toast.error('Failed to save chapters');
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		setSaving(true);
		try {
			await axiosConfig.delete(`${import.meta.env.VITE_APP_API_URL}/chapters/delete/${videoId}`);
			Toast.success('Chapters deleted successfully');
			onClose();
		} catch (err) {
			Toast.error('Failed to delete chapters');
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				style: {
					maxHeight: '90vh',
					overflow: 'auto'
				}
			}}
		>
			<DialogTitle>Manage Video Chapters</DialogTitle>
			<DialogContent>
				{loading ? (
					<Box display="flex" justifyContent="center" p={3}>
						<CircularProgress />
					</Box>
				) : (
					<Box display="flex" flexDirection="column" gap={3} pt={2}>
						{chapters.map((chapter, index) => (
							<Card key={index} variant="outlined">
								<CardContent>
									<Grid container spacing={2} alignItems="center">
										<Grid item xs={12} md={4}>
											<TextField
												fullWidth
												label="Chapter Title"
												value={chapter.title}
												onChange={(e) => handleChange(index, 'title', e.target.value)}
											/>
										</Grid>

										<Grid item xs={12} md={4}>
											<DurationPicker
												label="Chapter time"
												value={chapter.timecode || '00:00:00'}
												onChange={(newTime) => handleChange(index, 'timecode', newTime)}
												maxDuration={getMaxTime(videoDuration)}
											/>
										</Grid>

										<Grid item xs={12} md={4}>
											<Button
												variant="contained"
												onClick={() => generateThumbnail(index)}
												disabled={thumbnailLoading[index]}
												fullWidth
											>
												{thumbnailLoading[index] ? <CircularProgress size={24} color="inherit" /> : 'Generate Thumbnail'}
											</Button>
										</Grid>

									{chapter.thumbnail && (
										<Grid item xs={12}>
											<img
												src={chapter.thumbnail}
												alt="Chapter Thumbnail"
												style={{
													maxWidth: '100%',
													height: 'auto',
													maxHeight: '12.5rem',
													borderRadius: '0.5rem',
													border: '1px solid #ccc'
												}}
											/>
										</Grid>
									)}
									</Grid>
								</CardContent>

								<CardActions>
									{chapters.length > 1 && (
										<IconButton onClick={() => handleRemoveChapter(index)} color="error">
											<DeleteIcon />
										</IconButton>
									)}
								</CardActions>
							</Card>
						))}

						<Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddChapter}>
							Add Another Chapter
						</Button>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					Cancel
				</Button>
				<Button onClick={() => setDeleteConfirmOpen(true)} color="error" disabled={saving || chapters.length === 0}>
					Delete All
				</Button>
				<Button onClick={handleSave} variant="contained" disabled={saving || loading}>
					{saving ? <CircularProgress size={24} /> : 'Save'}
				</Button>
			</DialogActions>

			{/* Delete Confirmation Dialog */}
			<Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
				<DialogTitle>Delete Confirmation</DialogTitle>
				<DialogContent>
					<Typography>Are you sure you want to delete all chapters for this video?</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
					<Button onClick={() => { setDeleteConfirmOpen(false); handleDelete(); }} color="error" variant="contained">
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Dialog>
	);
};

export default ChaptersModal;
